import _ from "lodash";
import React, { useMemo } from "react";
import PropTypes from "prop-types";

import { Tooltip } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";
import classes from "./NotificationBadgesTooltip.scss";
import {
  PROJECT_GROUP_TYPE_PERSONAL,
  PROJECT_GROUP_TYPE_DP_EE,
  PROJECT_GROUP_TYPE_DP_TOK_ESSAY,
  PROJECT_GROUP_TYPE_DP_G4_PROJECT,
  PROJECT_GROUP_TYPE_DP_COLLABORATIVE_SCIENCES_PROJECT,
  PROJECT_GROUP_TYPE_COMMUNITY,
  PROJECT_GROUP_TYPE_CP_REFLECTIVE_PROJECT,
  PORTFOLIO_INSIGHTS_PROJECT_GROUP_TYPE_MAPPING,
} from "Projects/Constants/stringConstants";
import {
  POST_NOTIFICATION_BADGES_COUNT,
  COMMENT_NOTIFICATION_BADGES_COUNT,
  ACADEMIC_HONESTY_NOTIFICATION_BADGES_COUNT,
  ATTACHMENT_NOTIFICATION_BADGES_COUNT,
  REVIEW_NOTIFICATION_BADGES_COUNT,
  FEEDBACK_NOTIFICATION_BADGES_COUNT,
  ANNOTATION_NOTIFICATION_BADGES_COUNT,
  EXPERIENCE_NOTIFICATION_BADGES_COUNT,
  OTHER_CHANGES_BADGES_COUNT,
  INTERVIEW_NOTIFICATION_BADGES_COUNT,
  PENDING_APPROVAL_NOTIFICATION_BADGES_COUNT,
} from "Projects/components/NotificationBadgesTooltip/modules/stringConstants";

const NotificationBadgesTooltip = ({ children, ...props }) => {
  const {
    projectGroupType,
    notificationList,
    tooltipPlacement = "right",
  } = props;

  const { t } = useI18n();

  const notificationListInOrder = [
    {
      type: REVIEW_NOTIFICATION_BADGES_COUNT,
      count: _.get(notificationList, REVIEW_NOTIFICATION_BADGES_COUNT, 0),
    },
    {
      type: INTERVIEW_NOTIFICATION_BADGES_COUNT,
      count: _.get(notificationList, INTERVIEW_NOTIFICATION_BADGES_COUNT, 0),
    },
    {
      type: ACADEMIC_HONESTY_NOTIFICATION_BADGES_COUNT,
      count: _.get(
        notificationList,
        ACADEMIC_HONESTY_NOTIFICATION_BADGES_COUNT,
        0
      ),
    },
    {
      type: FEEDBACK_NOTIFICATION_BADGES_COUNT,
      count: _.get(notificationList, FEEDBACK_NOTIFICATION_BADGES_COUNT, 0),
    },
    {
      type: POST_NOTIFICATION_BADGES_COUNT,
      count: _.get(notificationList, POST_NOTIFICATION_BADGES_COUNT, 0),
    },
    {
      type: ATTACHMENT_NOTIFICATION_BADGES_COUNT,
      count: _.get(notificationList, ATTACHMENT_NOTIFICATION_BADGES_COUNT, 0),
    },
    {
      type: ANNOTATION_NOTIFICATION_BADGES_COUNT,
      count: _.get(notificationList, ANNOTATION_NOTIFICATION_BADGES_COUNT, 0),
    },
    {
      type: EXPERIENCE_NOTIFICATION_BADGES_COUNT,
      count: _.get(notificationList, EXPERIENCE_NOTIFICATION_BADGES_COUNT, 0),
    },
    {
      type: COMMENT_NOTIFICATION_BADGES_COUNT,
      count: _.get(notificationList, COMMENT_NOTIFICATION_BADGES_COUNT, 0),
    },
    {
      type: OTHER_CHANGES_BADGES_COUNT,
      count: _.get(notificationList, OTHER_CHANGES_BADGES_COUNT, 0),
    },
    {
      type: PENDING_APPROVAL_NOTIFICATION_BADGES_COUNT,
      count: _.get(
        notificationList,
        PENDING_APPROVAL_NOTIFICATION_BADGES_COUNT,
        0
      ),
    },
  ];

  const filteredNotificationsList = _.filter(
    notificationListInOrder,
    item => item.count > 0
  );

  const filteredNotificationsJSON = JSON.stringify(filteredNotificationsList);

  const tooltipData = useMemo(() => {
    return _.map(filteredNotificationsList, ({ type, count }) => {
      switch (type) {
        case REVIEW_NOTIFICATION_BADGES_COUNT: {
          return (
            <li>
              {t("project:new_review_request_with_count", {
                count,
              })}
            </li>
          );
        }
        case INTERVIEW_NOTIFICATION_BADGES_COUNT: {
          return (
            <li>
              {t("project:new_interview_with_count", {
                count,
              })}
            </li>
          );
        }
        case ACADEMIC_HONESTY_NOTIFICATION_BADGES_COUNT: {
          switch (projectGroupType) {
            case PROJECT_GROUP_TYPE_PERSONAL:
            case PROJECT_GROUP_TYPE_COMMUNITY:
              return (
                <li>
                  {t("project:new_meeting_note_with_count", {
                    count,
                  })}
                </li>
              );

            case PROJECT_GROUP_TYPE_DP_EE:
            case PROJECT_GROUP_TYPE_DP_G4_PROJECT:
            case PROJECT_GROUP_TYPE_DP_COLLABORATIVE_SCIENCES_PROJECT:
            case PROJECT_GROUP_TYPE_CP_REFLECTIVE_PROJECT:
              return (
                <li>
                  {t("project:new_reflection_with_count", {
                    count,
                  })}
                </li>
              );

            case PROJECT_GROUP_TYPE_DP_TOK_ESSAY:
              return (
                <li>
                  {t("project:new_interaction_with_count", {
                    count,
                  })}
                </li>
              );
            default:
              return (
                <li>
                  {t("project:new_academic_honesty_with_count", {
                    count,
                  })}
                </li>
              );
          }
        }
        case FEEDBACK_NOTIFICATION_BADGES_COUNT: {
          return (
            <li>
              {t("project:new_feedback_post_with_count", {
                count,
              })}
            </li>
          );
        }
        case POST_NOTIFICATION_BADGES_COUNT: {
          return (
            <li>
              {t("project:new_post_with_count", {
                count,
              })}
            </li>
          );
        }
        case ATTACHMENT_NOTIFICATION_BADGES_COUNT: {
          return (
            <li>
              {t("project:new_files_uploaded_with_count", {
                count,
              })}
            </li>
          );
        }
        case ANNOTATION_NOTIFICATION_BADGES_COUNT: {
          return (
            <li>
              {t("project:new_files_annotated_with_count", {
                count,
              })}
            </li>
          );
        }
        case EXPERIENCE_NOTIFICATION_BADGES_COUNT: {
          const { projectLabelPlural, projectLabel } =
            PORTFOLIO_INSIGHTS_PROJECT_GROUP_TYPE_MAPPING[projectGroupType] ??
            {};
          return (
            <li>
              {t("project:portfolio_new_project_notification_badge_tooltip", {
                count,
                projectType:
                  count > 1 ? t(projectLabelPlural) : t(projectLabel),
              })}
            </li>
          );
        }
        case COMMENT_NOTIFICATION_BADGES_COUNT: {
          return (
            <li>
              {t("project:new_comment_with_count", {
                count,
              })}
            </li>
          );
        }
        case OTHER_CHANGES_BADGES_COUNT: {
          return (
            <li>
              {t("project:other_changes_with_count", {
                count,
              })}
            </li>
          );
        }
        case PENDING_APPROVAL_NOTIFICATION_BADGES_COUNT: {
          return (
            <li>
              {t("project:pending_approval_with_count", {
                count,
              })}
            </li>
          );
        }
      }
    });
  }, [projectGroupType, filteredNotificationsJSON]);

  const renderTooltip = () => {
    return _.isEmpty(filteredNotificationsList) ? (
      <span>{children}</span>
    ) : (
      <Tooltip
        tooltip={
          <ul className={classes.notificationTooltipList}>{tooltipData}</ul>
        }
        placement={tooltipPlacement}
      >
        {children}
      </Tooltip>
    );
  };

  return renderTooltip();
};

NotificationBadgesTooltip.propTypes = {
  projectGroupType: PropTypes.string,
  notificationList: PropTypes.object,
};

export default NotificationBadgesTooltip;
